/* Native set discovery and purchase rules. Prices are Shopify market subunits. */
(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.HewnCurated = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';
  const FACETS = ['room', 'shape', 'color', 'style', 'subject', 'mood', 'orientation'];
  const normalize = value => String(value || '').toLocaleLowerCase().trim();
  function matches(item, state) {
    if (!FACETS.every(key => !state[key]?.length || state[key].some(value => (item.taxonomy?.[key] || []).includes(value)))) return false;
    const haystack = normalize([item.title, item.why, ...Object.values(item.taxonomy || {}).flat()].join(' '));
    return normalize(state.q).split(/\s+/).every(word => haystack.includes(word));
  }
  function facetValues(items, key, state) {
    const values = [...new Set(items.flatMap(item => item.taxonomy?.[key] || []))].sort((a, b) => a.localeCompare(b));
    const applicable = items.filter(item => matches(item, {...state, [key]: []}));
    return values.map(value => ({value, count: applicable.filter(item => (item.taxonomy?.[key] || []).includes(value)).length}));
  }
  function readState(url) {
    const params = new URL(url, 'https://hewnearth.com').searchParams;
    const state = {q: params.get('q') || '', columns: params.get('columns') === '3' ? 3 : 4};
    FACETS.forEach(key => state[key] = [...new Set(params.getAll(key).filter(Boolean))]);
    return state;
  }
  function writeState(url, state) {
    const next = new URL(url, 'https://hewnearth.com');
    [...FACETS, 'q', 'columns', 'page'].forEach(key => next.searchParams.delete(key));
    FACETS.forEach(key => (state[key] || []).forEach(value => next.searchParams.append(key, value)));
    if (state.q?.trim()) next.searchParams.set('q', state.q.trim());
    next.searchParams.set('columns', state.columns === 3 ? '3' : '4');
    return next.href;
  }
  function optionIndex(product, name) {
    return (product.options || []).findIndex(option => normalize(typeof option === 'string' ? option : option.name) === normalize(name));
  }
  function findVariant(product, selection) {
    const keys = ['size', 'material', 'frame'];
    return (product.variants || []).find(variant => keys.every(key => {
      const index = optionIndex(product, key);
      if (index < 0) return key === 'frame' && selection[key] === 'Unframed';
      return variant.options[index] === selection[key];
    })) || null;
  }
  function choiceValues(product, name, material) {
    const index = optionIndex(product, name), materialIndex = optionIndex(product, 'material');
    if (index < 0) return name.toLowerCase() === 'frame' ? ['Unframed'] : [];
    const variants = (product.variants || []).filter(variant => materialIndex >= 0 && variant.options[materialIndex] === material);
    return [...new Set(variants.map(variant => variant.options[index]))];
  }
  function validPrice(value) {
    if (!Number.isSafeInteger(value) || value < 0) throw new Error('A current product price is unavailable.');
    return value;
  }
  function promoPrice(value, percentage, currency) {
    const price = validPrice(value);
    const sale = globalThis.HewnEarthSale, config = globalThis.HewnEarthSaleConfig || {};
    if (sale?.enabled) return sale.price(price, currency, percentage);
    const amount = price * (100 - percentage) / 100;
    return ['USD', 'AUD', 'CAD', 'GBP', 'NZD'].includes(currency || config.currency || 'USD') ? Math.ceil(amount) : Math.round(amount);
  }
  function salePrice(value, saleEnabled) {
    const config = globalThis.HewnEarthSaleConfig || {};
    return saleEnabled ? promoPrice(value, Number(globalThis.HewnEarthSale?.rate ?? config.percent ?? config.rate ?? 20)) : validPrice(value);
  }
  function quote(variants, {saleEnabled = false, bundleEligible = true, currency} = {}) {
    const original = variants.reduce((sum, variant) => sum + validPrice(variant?.price), 0);
    if (saleEnabled) {
      const config = globalThis.HewnEarthSaleConfig || {};
      const total = promoPrice(original, Number(globalThis.HewnEarthSale?.multiRate ?? config.multiPercent ?? 25), currency);
      return {original, total, saving: original - total};
    }
    // Native order discounts floor the aggregate discount allocation in currency subunits.
    const total = bundleEligible ? original - Math.floor(original * 5 / 100) : original;
    return {original, total, saving: original - total};
  }
  function framePremium(product, variant, {saleEnabled = false} = {}) {
    if (!variant) return null;
    const baseline = findVariant(product, {size: variant.options[optionIndex(product, 'size')], material: variant.options[optionIndex(product, 'material')], frame: 'Unframed'});
    return baseline ? salePrice(variant.price, saleEnabled) - salePrice(baseline.price, saleEnabled) : null;
  }
  function money(value, currency, locale) {
    return new Intl.NumberFormat(locale || undefined, {style: 'currency', currency, minimumFractionDigits: value % 100 ? undefined : 0}).format(validPrice(value) / 100);
  }
  function buildCartPayload(variants, attribution) {
    if (variants.length !== 2 || variants.some(variant => !variant?.available || !Number.isSafeInteger(Number(variant.id)) || Number(variant.id) <= 0)) throw new Error('One of these selections is unavailable. Please choose another size or frame.');
    variants.forEach(variant => validPrice(variant.price));
    return {items: variants.map(variant => ({id: Number(variant.id), quantity: 1, properties: {_Curated_set: String(attribution)}}))};
  }
  function createAddController() {
    let pending = false;
    return async function (cart, payload, button) {
      if (pending) return false;
      if (!cart || typeof cart.addItems !== 'function') throw new Error('The cart is still loading. Please try again.');
      pending = true;
      try { await cart.addItems(payload, button); return true; }
      finally { pending = false; }
    };
  }
  async function loadCatalog(firstUrl, fetcher, expectedCount, progress) {
    const initialUrl = new URL(firstUrl, typeof location === 'object' ? location.origin : 'https://hewnearth.com').href;
    const origin = new URL(initialUrl).origin, visited = new Set(), items = [], identities = new Set();
    let currency;
    async function readPage(url) {
      if (visited.has(url) || visited.size >= 30 || new URL(url).origin !== origin) throw new Error('The set catalog could not finish loading.');
      visited.add(url);
      let response;
      for (let attempt = 0; attempt < 3; attempt++) {
        response = await fetcher(url, {headers: {Accept: 'text/html'}, credentials: 'same-origin'});
        if (response.status !== 429 || attempt === 2) break;
        const retryAfter = response.headers?.get?.('Retry-After')?.trim();
        let delay = (attempt + 1) * 1000;
        if (retryAfter) {
          const requested = /^\d+(?:\.\d+)?$/.test(retryAfter) ? Number(retryAfter) * 1000 : Date.parse(retryAfter) - Date.now();
          if (Number.isFinite(requested)) delay = requested;
        }
        await new Promise(resolve => setTimeout(resolve, Math.min(10000, Math.max(0, delay))));
      }
      if (!response.ok) throw new Error(`The set catalog could not load (${response.status}).`);
      let raw = await response.text();
      const embedded = raw.match(/<script[^>]*data-curated-catalog[^>]*>([\s\S]*?)<\/script>/i);
      if (embedded) raw = embedded[1];
      const data = JSON.parse(raw);
      if (!Array.isArray(data.items) || !data.currency) throw new Error('The set catalog response is incomplete.');
      return data;
    }
    function appendPage(data) {
      if (currency && currency !== data.currency) throw new Error('The set catalog response is incomplete.');
      const batch = new Set();
      data.items.forEach(item => {
        if (!Number.isInteger(item.setNumber) || identities.has(item.setNumber) || batch.has(item.setNumber) || !item.url || !item.image || !item.title) throw new Error('The set catalog response is incomplete.');
        validPrice(item.price); batch.add(item.setNumber);
      });
      currency = data.currency;
      data.items.forEach(item => { identities.add(item.setNumber); items.push(item); });
      if (progress) progress(items.length);
    }
    function pagination(data, url, contract, expectedPage) {
      const complete = ['page', 'totalPages', 'pageParam'].every(key => data[key] !== undefined);
      if (!complete && !contract) return null;
      if (!complete || !Number.isInteger(data.page) || !Number.isInteger(data.totalPages) || data.page < 1 || data.totalPages < data.page || data.totalPages > 30 || typeof data.pageParam !== 'string' || !/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(data.pageParam)) throw new Error('The set catalog pagination is incomplete.');
      if (contract && (data.page !== expectedPage || data.totalPages !== contract.totalPages || data.pageParam !== contract.pageParam)) throw new Error('The set catalog pagination is incomplete.');
      const current = new URL(url), currentValues = current.searchParams.getAll(data.pageParam);
      if (currentValues.length && (currentValues.length !== 1 || currentValues[0] !== String(data.page))) throw new Error('The set catalog pagination is incomplete.');
      if (data.page === data.totalPages) {
        if (data.nextUrl) throw new Error('The set catalog pagination is incomplete.');
      } else {
        if (!data.nextUrl) throw new Error('The set catalog pagination is incomplete.');
        const next = new URL(data.nextUrl, url), values = next.searchParams.getAll(data.pageParam);
        if (next.origin !== origin || values.length !== 1 || values[0] !== String(data.page + 1)) throw new Error('The set catalog pagination is incomplete.');
      }
      return data;
    }
    const first = await readPage(initialUrl), contract = pagination(first, initialUrl);
    appendPage(first);
    if (contract && contract.page < contract.totalPages) {
      const base = new URL(contract.nextUrl, initialUrl);
      let nextPage = contract.page + 1, failed = false, failure;
      async function worker() {
        while (!failed && nextPage <= contract.totalPages) {
          const page = nextPage++, url = new URL(base);
          url.searchParams.set(contract.pageParam, String(page));
          try {
            const data = await readPage(url.href);
            pagination(data, url.href, contract, page);
            if (!failed) appendPage(data);
          } catch (error) {
            if (!failed) { failed = true; failure = error; }
          }
        }
      }
      await Promise.all(Array.from({length: Math.min(4, contract.totalPages - contract.page)}, worker));
      if (failed) throw failure;
    } else {
      let url = first.nextUrl ? new URL(first.nextUrl, initialUrl).href : null;
      while (url) {
        const data = await readPage(url);
        pagination(data, url); appendPage(data);
        url = data.nextUrl ? new URL(data.nextUrl, url).href : null;
      }
    }
    if (expectedCount && items.length !== expectedCount) throw new Error('The set catalog is incomplete. Please try again.');
    items.sort((a, b) => a.setNumber - b.setNumber);
    return {items, currency};
  }
  return {FACETS, matches, facetValues, readState, writeState, optionIndex, findVariant, choiceValues, quote, framePremium, money, buildCartPayload, createAddController, loadCatalog};
});
